import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CALENDLY_DEMO_URL } from "@/constants/links";

const NAV_LINKS = [
  { label: "Products", to: "/products" },
  { label: "Solutions", to: "/solutions" },
  { label: "About Us", to: "/about" },
  { label: "Blogs", to: "/blogs" },
  { label: "Contact Us", to: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 px-4 pt-4 md:px-6">
      {/* Floating pill bar */}
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between rounded-full bg-white/95 pl-6 pr-2.5 shadow-xl shadow-neutral-900/10 ring-1 ring-neutral-900/5 backdrop-blur-md">
        <div className="flex items-center gap-12">
          <Logo />

          {/* Desktop links */}
          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`relative py-1 text-sm font-medium transition-colors after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-200 hover:text-primary hover:after:scale-x-100 ${
                    isActive ? "text-primary after:scale-x-100" : "text-neutral-700"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Right side */}
        <div className="hidden items-center gap-2 lg:flex">
          <Link
            to="/login"
            className="px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:text-primary"
          >
            Login
          </Link>
          <Link
            to="/signup"
            className="rounded-full bg-neutral-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary"
          >
            Try For Free
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="rounded-full p-2.5 text-neutral-700 hover:bg-neutral-100 lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu — floating card below the pill */}
      {mobileOpen && (
        <div className="mx-auto mt-2 max-h-[calc(100vh-6.5rem)] max-w-6xl overflow-y-auto rounded-3xl bg-white/95 px-6 pb-6 pt-2 shadow-xl shadow-neutral-900/10 ring-1 ring-neutral-900/5 backdrop-blur-md lg:hidden">
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.label}
                to={link.to}
                className={`block py-3 text-base font-medium ${
                  isActive ? "text-primary" : "text-neutral-800"
                }`}
              >
                {link.label}
              </Link>
            );
          })}

          <div className="mt-4 flex flex-col gap-3 border-t border-neutral-200 pt-5">
            <Link to="/login" className="text-center text-base font-medium text-neutral-800">
              Login
            </Link>
            <Link
              to="/signup"
              className="rounded-full bg-neutral-900 px-5 py-3 text-center text-base font-semibold text-white"
            >
              Try For Free
            </Link>
            <a
              href={CALENDLY_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-neutral-300 px-5 py-3 text-center text-base font-medium text-neutral-800"
            >
              Book Demo
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
